/**
 * Channels Hooks
 * Jotai atoms and hooks for channel management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { channelService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { UpdateChannelDto } from '@/types';

// Query atoms
const channelsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['channels', buScopeKey(get)],
  queryFn: () => channelService.getChannels(buScopeParams(get)),
  staleTime: 60 * 1000
}));

/**
 * Brand-scoped, like every other channel read.
 *
 * Unscoped, the backend's `buScopeFilter` contributes nothing and `/channels?isDefault=true`
 * returns the default channels of EVERY brand on the account. Channel Based Filtering
 * rendered all of them, so account 1 showed its four channels thirteen times over — Email
 * and Social repeating down the page, with nothing on a row to say which brand it was.
 *
 * Worse than noise: the toggles wrote to whichever duplicate the operator happened to
 * click, so turning Email off could disable it for a brand they were not looking at.
 *
 * The brand goes in the query KEY as well as the request. Switching brands no longer
 * clears the cache, so a key without it serves the previous brand's rows.
 */
const defaultChannelsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['channels', 'default', buScopeKey(get)],
  queryFn: () => channelService.getDefaultChannels(buScopeParams(get))
}));

// Mutation atoms
const updateChannelMutationAtom = atomWithMutation(() => ({
  mutationKey: ['channels', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateChannelDto }) => channelService.updateChannel(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['channels']);
    invalidateQueries(['channels', variables.id]);
  }
}));

// Hooks
export function useChannels() {
  const [query] = useAtom(channelsQueryAtom);
  return query;
}

export function useDefaultChannels() {
  const [query] = useAtom(defaultChannelsQueryAtom);
  return query;
}

export function useUpdateChannel() {
  const [mutation] = useAtom(updateChannelMutationAtom);
  return mutation;
}
